import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { onboardingAPI } from '../../api';

function DocumentUpload({ token, formData, onNext, onBack }) {
  const [uploads, setUploads] = useState({
    id_front: null,
    id_back: null,
    selfie: null
  });
  const [uploading, setUploading] = useState({});
  const [errors, setErrors] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});

  const uploadDocument = async (file, docType) => {
    setUploading(prev => ({ ...prev, [docType]: true }));
    setErrors(prev => ({ ...prev, [docType]: null }));

    try {
      await onboardingAPI.uploadDocument(token, file, docType);
      setUploads(prev => ({ ...prev, [docType]: file }));
    } catch (err) {
      setErrors(prev => ({ ...prev, [docType]: err.response?.data?.detail || 'Upload failed' }));
    } finally {
      setUploading(prev => ({ ...prev, [docType]: false }));
    }
  };

  // Create dropzone hooks at component level
  const idFrontDropzone = useDropzone({
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    },
    maxSize: 10 * 1024 * 1024,
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        uploadDocument(acceptedFiles[0], 'id_front');
      }
    },
    onDropRejected: (fileRejections) => {
      const error = fileRejections[0]?.errors[0];
      if (error?.code === 'file-too-large') {
        setErrors(prev => ({ ...prev, id_front: 'File too large. Maximum size is 10MB.' }));
      } else if (error?.code === 'file-invalid-type') {
        setErrors(prev => ({ ...prev, id_front: 'Invalid file type. Only JPG and PNG are allowed.' }));
      }
    }
  });

  const idBackDropzone = useDropzone({
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    },
    maxSize: 10 * 1024 * 1024,
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        uploadDocument(acceptedFiles[0], 'id_back');
      }
    },
    onDropRejected: (fileRejections) => {
      const error = fileRejections[0]?.errors[0];
      if (error?.code === 'file-too-large') {
        setErrors(prev => ({ ...prev, id_back: 'File too large. Maximum size is 10MB.' }));
      } else if (error?.code === 'file-invalid-type') {
        setErrors(prev => ({ ...prev, id_back: 'Invalid file type. Only JPG and PNG are allowed.' }));
      }
    }
  });

  const selfieDropzone = useDropzone({
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    },
    maxSize: 10 * 1024 * 1024,
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        uploadDocument(acceptedFiles[0], 'selfie');
      }
    },
    onDropRejected: (fileRejections) => {
      const error = fileRejections[0]?.errors[0];
      if (error?.code === 'file-too-large') {
        setErrors(prev => ({ ...prev, selfie: 'File too large. Maximum size is 10MB.' }));
      } else if (error?.code === 'file-invalid-type') {
        setErrors(prev => ({ ...prev, selfie: 'Invalid file type. Only JPG and PNG are allowed.' }));
      }
    }
  });

  const renderDropzone = (dropzone, docType, label, description) => {
    const { getRootProps, getInputProps, isDragActive } = dropzone;
    
    return (
      <div className="form-group">
        <label>{label} *</label>
        <div
          {...getRootProps()}
          data-testid={`dropzone-${docType}`}
          style={{
            border: `2px dashed ${isDragActive ? 'var(--accent-teal)' : uploads[docType] ? 'var(--success-green)' : 'var(--bg-gray-200)'}`,
            borderRadius: '0.375rem',
            padding: '2rem',
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: isDragActive ? 'var(--accent-teal-lighter)' : 'var(--bg-gray-50)',
            transition: 'all 0.2s ease'
          }}
        >
          <input {...getInputProps()} />
          
          {uploading[docType] ? (
            <div>
              <div className="spinner" style={{ margin: '0 auto 1rem' }}></div>
              <p>Uploading...</p>
            </div>
          ) : uploads[docType] ? (
            <div>
              <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>✓</div>
              <p style={{ color: 'var(--success-green)', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                {uploads[docType].name}
              </p>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Click to replace</p>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>📎</div>
              <p style={{ marginBottom: '0.5rem' }}>
                {isDragActive ? 'Drop file here' : 'Drag & drop or click to upload'}
              </p>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>{description}</p>
            </div>
          )}
        </div>
        {errors[docType] && <p className="error-message">{errors[docType]}</p>}
      </div>
    );
  };

  const handleContinue = () => {
    const newErrors = {};
    
    if (!uploads.id_front) newErrors.id_front = 'Driver licence front is required';
    if (!uploads.id_back) newErrors.id_back = 'Driver licence back is required';
    if (!uploads.selfie) newErrors.selfie = 'Selfie with licence is required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    onNext({});
  };

  return (
    <div className="card fade-in">
      <h2>ID Verification Documents</h2>
      <p style={{ color: 'var(--text-muted)', marginBottom: '2rem' }}>
        To comply with ATO requirements, we need to verify your identity. Please upload the following documents:
      </p>

      <div className="alert alert-info" style={{ marginBottom: '2rem' }}>
        <strong>📷 Photo Tips</strong>
        <ul style={{ margin: '0.5rem 0 0 1.5rem', paddingLeft: '0' }}>
          <li>Ensure all text is clear and readable</li>
          <li>Take photos in good lighting</li>
          <li>Avoid glare and shadows</li>
          <li>File size must be under 10MB</li>
          <li>Accepted formats: JPG, PNG</li>
        </ul>
      </div>

      {renderDropzone(
        idFrontDropzone,
        'id_front',
        'Driver Licence - Front',
        'Clear photo of the front of your driver licence'
      )}

      {renderDropzone(
        idBackDropzone,
        'id_back',
        'Driver Licence - Back',
        'Clear photo of the back of your driver licence'
      )}

      {renderDropzone(
        selfieDropzone,
        'selfie',
        'Selfie with Licence',
        'Photo of yourself holding your driver licence next to your face'
      )}

      <div className="alert alert-warning" style={{ marginTop: '2rem' }}>
        <strong>⚠️ Privacy Notice</strong>
        <p style={{ margin: '0.5rem 0 0 0' }}>
          Your ID documents are securely encrypted and stored. They will only be accessed by authorized ST Books staff for verification purposes. You can request deletion after verification is complete.
        </p>
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
        <button 
          type="button" 
          className="btn btn-outline"
          data-testid="back-button"
          onClick={onBack}
        >
          ← Back
        </button>
        <button 
          type="button" 
          className="btn btn-primary"
          data-testid="continue-button"
          onClick={handleContinue}
          style={{ flex: 1 }}
        >
          Continue to Review →
        </button>
      </div>
    </div>
  );
}

export default DocumentUpload;